import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { useEffect, useState, lazy } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Switch, Redirect } from 'react-router-dom';
import 'swiper/css';

import './translations/locales';
import './App.scss';

const Home = lazy(() => import('./pages/home'));
const ArticlePage = lazy(() => import('./pages/articlePage'));
const ChancePage = lazy(() => import('./pages/chancePage'));
const KnowledgeBank = lazy(() => import('./pages/knowledgeBank/KnowledgeBank/KnowledgeBank'));
const KnowledgeBankArticles = lazy(() => import('./pages/knowledgeBankArticle'));
const ProfileLayout = lazy(() => import('./pages/profile'));
const ProfileSettings = lazy(() => import('./pages/profileSettings'));
const ShopMiddleware = lazy(() => import('./pages/shopMiddleware'));
const AuthPage = lazy(() => import('./pages/auth'));
const NoBonusGamesInfo = lazy(() => import('./pages/noBonusGamesInfo'));
const BonusShopCategory = lazy(() => import('./pages/bonusShopCategory'));
const SuccessfulProductOrder = lazy(() => import('./pages/successfulProductOrder'));
const QuizPage = lazy(() => import('./pages/quiz'));
const MainKBBrands = lazy(() => import('./pages/KBBrands'));
const QuestionOfTheDayPage = lazy(() => import('./pages/quiz/questionOfTheDay/questionOfTheDayPage'));
const SpecialQuizPage = lazy(() => import('./pages/quiz/specialQuiz/specialQuizPage'));
const EventModePage = lazy(() => import('./pages/quiz/EventMode/EventModePage'));
const SpecialQuizNoBonusGame = lazy(() => import('./pages/quiz/specialQuiz/specialQuizNoBonusGame'));
const SoloModePage = lazy(() => import('./pages/quiz/soloMode/soloModePage'));
const SingleModePage = lazy(() => import('./pages/quiz/singleMode/singleModePage'));
const DailyRewardsPage = lazy(() => import('./pages/quiz/DailyRewards/DailyRewardsPage'));
const SalesTipsPage = lazy(() => import('./pages/salesTips/salesTipsPage'));
const TipStepsPage = lazy(() => import('./pages/salesTips/tipStepsPage'));
const MainPage = lazy(() => import('./pages/mainPage'));
const TradePromotionsPage = lazy(() => import('./pages/tradePromotions'));
const TradePromotionDetailsPage = lazy(() => import('./pages/tradePromotionDetails'));
const MultibrandQuizPage = lazy(() => import('./pages/quiz/MultibrandQuiz/multibrandQuizPage'));
const AddressRegistration = lazy(() => import('./pages/addressRegistration'));
const M10 = lazy(() => import('./pages/Cashout/M10/M10'));
const BannerPage = lazy(() => import('./pages/bannerPage'));
const Challenges = lazy(() => import('./pages/challenge/containers/challenges/Challenges'));
const MonthlyConfirmationPage = lazy(() => import('./pages/monthlyConfirmation'));
const MonthlyReward = lazy(() => import('./pages/monthlyReward'));
const VoucherDetails = lazy(() => import('./pages/VoucherDetails/VoucherDetails'));
const SupportStub = lazy(() => import('./pages/chat/containers/supportStub/SupportStub'));
const Challenge = lazy(() => import('./pages/challenge/Challenge'));
const NoBonusSurveyComponent = lazy(() => import('./pages/quiz/Survey/noBonusSurveyComponent'));
const EsignRedirectPage = lazy(() => import('./pages/auth/esignPage/esignRedirectPage'));
const Notifications = lazy(() => import('./pages/chat/containers/notifications/Notifications'));
const TicketChat = lazy(() => import('./pages/chat/containers/ticketChat/TicketChat'));
const Support = lazy(() => import('./pages/chat/containers/supportChat/SupportChat'));
const QuestChallenge = lazy(() => import('./pages/challenge/containers/challenge/QuestChallenge'));
const BrandInformationSlider = lazy(() => import('./pages/KBBrands/BrandInforamtionSlider/BrandInformationSlider'));
const QuickTips = lazy(() => import('./pages/quickTips/QuickTips'));
const QuickTipResult = lazy(() => import('./pages/quickTips/result/QuickTipResult'));
const ChanceList = lazy(() => import('./pages/chancePage/ChanceList/ChanceList'));
const SalesRepTransferPage = lazy(() => import('./pages/salesRepTransfer'));
const SalesRepTransferHistoryPage = lazy(() => import('./pages/salesRepTransferHistory'));
const Modals = lazy(() => import('./components/containers/Modal/Modals'));

import { ErrorPage, SuccessPage } from './views';
import { Routes } from './const';
import { AuthorizedRoute } from './components/AuthorizedRoute';
import { NonAuthorizedRoute } from './components/NonAuthorizedRoute/NonAuthorizedRoute';
import { isAuthorizedSelector } from './redux/auth/authSelectors';
import { serviceActions } from './redux/service/serviceSlice';
import { useDeviceOrientation } from './hooks/useDeviceOrientationHook';
import { analyticHelper } from './helpers/analyticHelper';
import { checkIsIphoneX } from './helpers/checkIsIphoneX';
import { IosAddToHomescreenBaner } from './components/common/IosAddToHomescreenBaner/IosAddToHomescreenBaner';
import NotificationMessage from './components/common/NotificationMessage';
import { ChatRoutes } from './const';
import { CONFIG_KEYS, isFeatureEnabled } from './config';
import { useTheme } from './hooks/useTheme';
import usePushSubscription from './hooks/usePushSubscription';
import { settingsActions } from './redux/settings/settingsSlice';
import { GAME_MODES } from './redux/settings/settingsConstants';

function App(props) {
  useTheme();

  const dispatch = useDispatch();
  const { isAuthorized, location } = props;
  const theme = useSelector((state) => state.service.theme);
  const [showIosBaner, setShowIosBaner] = useState(false);

  useDeviceOrientation(dispatch);
  usePushSubscription();

  useEffect(() => {
    dispatch(serviceActions.getLinks());
    dispatch(serviceActions.setThemeLocal());
    dispatch(settingsActions.setGameMode(GAME_MODES.SOLO));
    analyticHelper.sessionLaunch();
  }, [dispatch]);

  useEffect(() => {
    const isIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };
    const isInStandaloneMode = () => 'standalone' in window.navigator && window.navigator.standalone;

    if (isIos() && !isInStandaloneMode() && isFeatureEnabled(CONFIG_KEYS.IOS_ADD_TO_HOME_SCREEN_BANNER_ENABLED)) {
      setShowIosBaner(true);
      setTimeout(() => {
        setShowIosBaner(false);
      }, 10000);
    }
  }, []);

  const getChatRoutes = () => {
    return [
      <AuthorizedRoute
        key="supportChat"
        path={`${ChatRoutes.Support}/:id`}
        component={isFeatureEnabled(CONFIG_KEYS.SUPPORT_WITH_TICKETS_ENABLED) ? TicketChat : SupportStub}
        isAuthorized={isAuthorized}
        withNavigation
      />,
      <AuthorizedRoute
        key="support"
        path={ChatRoutes.Support}
        component={isFeatureEnabled(CONFIG_KEYS.SUPPORT_WITH_TICKETS_ENABLED) ? Support : SupportStub}
        isAuthorized={isAuthorized}
        withNavigation
        exact
      />,
      <AuthorizedRoute
        key="notifications"
        path={ChatRoutes.Notifications}
        component={Notifications}
        isAuthorized={isAuthorized}
        withNavigation
        exact
      />,
    ];
  };

  return (
    <div className={classnames(`theme${theme.id}`, checkIsIphoneX() ? 'iphoneXTheme' : '')}>
      <Switch location={location}>
        <AuthorizedRoute path={Routes.MainRoute} component={MainPage} isAuthorized={isAuthorized} />
        <AuthorizedRoute
          exact
          path={Routes.TradePromotionsRoute}
          component={TradePromotionsPage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          exact
          path={`${Routes.TradePromotionsRoute}/:id`}
          component={TradePromotionDetailsPage}
          isAuthorized={isAuthorized}
        />

        <NonAuthorizedRoute exact={true} path={'/'} component={AuthPage} isAuthorized={isAuthorized} />
        <NonAuthorizedRoute path={'/login'} component={EsignRedirectPage} isAuthorized={isAuthorized} />
        {getChatRoutes()}
        <AuthorizedRoute
          path={Routes.MonthlyConfirmation}
          component={MonthlyConfirmationPage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute exact path={Routes.M10MainRoute} component={M10} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.SuccessPage} component={SuccessPage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.ErrorPage} component={ErrorPage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.BonusShopRoute} exact component={ShopMiddleware} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.MyVouchers} component={ShopMiddleware} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.PrizesRoute} component={ShopMiddleware} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.BalanceRoute} component={ShopMiddleware} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.BrandsRoute} component={Home} isAuthorized={isAuthorized} />
        <AuthorizedRoute
          path={Routes.AddressRegistration}
          component={AddressRegistration}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={`${Routes.BonusShopRoute}/:id`}
          component={BonusShopCategory}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute path={`${Routes.VoucherDetails}/:id`} component={VoucherDetails} isAuthorized={isAuthorized} />
        <AuthorizedRoute
          path={`${Routes.NoBonusGamesRoutes}/:brandId`}
          component={NoBonusGamesInfo}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={`${Routes.NoBonusSpecialQuizRoute}/:brandId`}
          component={SpecialQuizNoBonusGame}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={Routes.SuccessfulOrderRoute}
          component={SuccessfulProductOrder}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute path={Routes.KnowledgeBankRoute} exact component={KnowledgeBank} isAuthorized={isAuthorized} />
        <AuthorizedRoute
          path={Routes.noBonusSurveyRoute}
          component={NoBonusSurveyComponent}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={`${Routes.KnowledgeBankRoute}/:id/brands`}
          exact
          component={MainKBBrands}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          exact
          path={Routes.KnowledgeBankRoute + Routes.SalesTips}
          component={SalesTipsPage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          exact
          path={`${Routes.KnowledgeBankRoute + Routes.SalesTips}/:id`}
          component={TipStepsPage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          exact
          path={`${Routes.KnowledgeBankRoute}/:id`}
          component={KnowledgeBankArticles}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          exact
          path={`${Routes.KnowledgeBankRoute}/:id/brands/:brand_id`}
          component={KnowledgeBankArticles}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={`${Routes.BrandInformation}/:id`}
          component={BrandInformationSlider}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={Routes.QuestionOfTheDayRoute}
          component={QuestionOfTheDayPage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute
          path={`${Routes.SpecialQuizRoute}/:brandId`}
          component={SpecialQuizPage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute path={Routes.DailyRewardsRoute} component={DailyRewardsPage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={`${Routes.QuizRoute}/:brandId`} component={QuizPage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.QuickTips} component={QuickTips} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.QuickTipsResult} isAuthorized={isAuthorized} component={QuickTipResult} />
        <AuthorizedRoute path={Routes.ArticleRoute} component={ArticlePage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.BannerRoute} component={BannerPage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={`${Routes.ChanceRoute}/:id`} component={ChancePage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.ChanceList} component={ChanceList} isAuthorized={isAuthorized} />
        <AuthorizedRoute
          path={Routes.ProfileRoute}
          component={ProfileLayout}
          isAuthorized={isAuthorized}
          withNavigation
        />
        <AuthorizedRoute
          path={Routes.SalesRepTransferHistoryRoute}
          component={SalesRepTransferHistoryPage}
          isAuthorized={isAuthorized}
          exact
        />
        <AuthorizedRoute
          path={Routes.SalesRepTransferRoute}
          component={SalesRepTransferPage}
          isAuthorized={isAuthorized}
          exact
        />
        <AuthorizedRoute path={Routes.ProfileSettingsRoute} component={ProfileSettings} isAuthorized={isAuthorized} />
        <AuthorizedRoute
          path={`${Routes.SoloMode}/:brandId/:packId?`}
          component={SoloModePage}
          isAuthorized={isAuthorized}
        />
        <AuthorizedRoute path={Routes.ChallengeRoute} component={Challenge} isAuthorized={isAuthorized} exact />
        <AuthorizedRoute path={Routes.MonthlyReward} component={MonthlyReward} isAuthorized={isAuthorized} exact />

        {isFeatureEnabled(CONFIG_KEYS.TEAM_CHALLENGES_ENABLED) && (
          <>
            <AuthorizedRoute path={Routes.ChallengesRoute} component={Challenges} isAuthorized={isAuthorized} />
            <AuthorizedRoute
              path={`${Routes.ChallengeRoute}/:challengeId`}
              component={QuestChallenge}
              isAuthorized={isAuthorized}
              exact
            />
          </>
        )}
        <AuthorizedRoute path={Routes.EventModeRoute} component={EventModePage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.SingleMode} component={SingleModePage} isAuthorized={isAuthorized} />
        <AuthorizedRoute path={Routes.MultibrandQuizRoute} component={MultibrandQuizPage} isAuthorized={isAuthorized} />
        <Redirect to={Routes.MainRoute} />
      </Switch>
      {showIosBaner && <IosAddToHomescreenBaner />}
      <Modals />
      <NotificationMessage />
    </div>
  );
}

App.propTypes = {
  isAuthorized: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  isAuthorized: isAuthorizedSelector(state),
});

export default connect(mapStateToProps)(App);
